import { PLAN_DEFAULT_SIZE, type CardSize } from './cardPositions';

/**
 * The geometry of resizing the corner plan from a corner (`observers-corner-plan-resize`, FR1,
 * FR11–FR13). Pure, in the cards layer's own pixels: `left`/`top` from the layer's top-left, as
 * `MovableCard` places its slot.
 */

export type Corner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
export const CORNERS: readonly Corner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

export interface CardRect {
  left: number;
  top: number;
  w: number;
  h: number;
}

/** Each corner's zone: a 20 × 20 px square at the corner, reaching 4 px outside the card (FR1). */
const ZONE = { size: 20, outside: 4 };

const isLeft = (c: Corner) => c === 'top-left' || c === 'bottom-left';
const isTop = (c: Corner) => c === 'top-left' || c === 'top-right';

/** The corner whose zone holds the point, or null. */
export const cornerAt = (p: { x: number; y: number }, rect: CardRect): Corner | null => {
  const inside = ZONE.size - ZONE.outside;
  const right = rect.left + rect.w;
  const bottom = rect.top + rect.h;
  const nearLeft = p.x >= rect.left - ZONE.outside && p.x <= rect.left + inside;
  const nearRight = p.x >= right - inside && p.x <= right + ZONE.outside;
  const nearTop = p.y >= rect.top - ZONE.outside && p.y <= rect.top + inside;
  const nearBottom = p.y >= bottom - inside && p.y <= bottom + ZONE.outside;
  if (nearTop && nearLeft) return 'top-left';
  if (nearTop && nearRight) return 'top-right';
  if (nearBottom && nearLeft) return 'bottom-left';
  if (nearBottom && nearRight) return 'bottom-right';
  return null;
};

/** The resize cursor for a corner (FR7). */
export const cursorFor = (corner: Corner): 'nwse-resize' | 'nesw-resize' =>
  corner === 'top-left' || corner === 'bottom-right' ? 'nwse-resize' : 'nesw-resize';

export interface SizeLimits {
  min: CardSize;
  max: CardSize;
}

/**
 * Smallest is the card's default size (220 × 146 for the plan); largest is half the picture's width
 * and 60 % of its height, and never more than fits inside the layer's inset. Where the largest would be smaller than the
 * smallest (a very small picture), the smallest wins, as the card is drawn today (FR13).
 */
export const sizeLimits = (
  layer: { width: number; height: number },
  inset: number,
  min: CardSize = PLAN_DEFAULT_SIZE,
): SizeLimits => {
  return {
    min,
    max: {
      w: Math.max(min.w, Math.min(layer.width * 0.5, layer.width - 2 * inset)),
      h: Math.max(min.h, Math.min(layer.height * 0.6, layer.height - 2 * inset)),
    },
  };
};

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

/** A stored size as drawn for the picture as it is now (FR19). */
export const clampSize = (size: CardSize, limits: SizeLimits): CardSize => ({
  w: clamp(size.w, limits.min.w, limits.max.w),
  h: clamp(size.h, limits.min.h, limits.max.h),
});

/**
 * The card after dragging `corner` by (`dx`, `dy`) from `start`: only that corner's two edges move,
 * the opposite corner stays where it was (FR11), the size stays inside the limits (FR13), and the
 * moving edges stop at the layer's inset. Where even the smallest size does not fit, it still wins.
 */
export const resizeRect = (
  start: CardRect,
  corner: Corner,
  dx: number,
  dy: number,
  limits: SizeLimits,
  layer: { width: number; height: number },
  inset: number,
): CardRect => {
  const right = start.left + start.w;
  const bottom = start.top + start.h;
  // How far the moving edge may go before it meets the layer's inset.
  const roomW = isLeft(corner) ? right - inset : layer.width - inset - start.left;
  const roomH = isTop(corner) ? bottom - inset : layer.height - inset - start.top;
  const wantW = isLeft(corner) ? start.w - dx : start.w + dx;
  const wantH = isTop(corner) ? start.h - dy : start.h + dy;
  const w = Math.max(limits.min.w, Math.min(clamp(wantW, limits.min.w, limits.max.w), roomW));
  const h = Math.max(limits.min.h, Math.min(clamp(wantH, limits.min.h, limits.max.h), roomH));
  return {
    left: isLeft(corner) ? right - w : start.left,
    top: isTop(corner) ? bottom - h : start.top,
    w,
    h,
  };
};
